/**
 * Phase 6 Vendor-Port Wave 1 — UV-C selector static data (§33.6).
 *
 * The ~25 catalog collection URLs (verbatim from
 * components/uvc/collection/*.js urlRoots), the CMS-path mapping
 * (mergeIdsWithCMS / dataForRating PARAMETER_CMS_PATHS_MAPPING), the dependency
 * defaults (the legacy DependenciesHandler default_values), and the
 * required-field set (REQUIRED_FIELDS[42]). Kept out of the component so the
 * 1,935-line legacy view maps to a readable React file.
 *
 * mfg 42 = "UV Resources" — the ONLY UVC manufacturer (the legacy REQUIRED_/
 * FIRST_SET_FIELDS maps are keyed solely on 42; the selector state seeds
 * mfg_id:42).
 */

import type { DependencyGraphSpec } from './useDependencyGraph';

export const UVC_MFG_ID = 42;

/** part_id appended to the product_series_id params GET (legacy loadParams). */
export const UVC_PART_ID = 96;

/** intlk_contact_controller_series_id sentinel for the inter_lock_switch GET. */
export const INTLK_CONTACT_CONTROLLER_SERIES_ID = 832;

/**
 * Catalog collection URLs (absolute) — getMasterListByUrl fetches each. CMS
 * (cms/1/...) lists are mfg-namespaced under cms id 1; master lists are
 * master/v1.0.0. Verbatim from collection/*.js urlRoots ([NEG]#75 dead
 * coil_master_collection excluded).
 */
export const UVC_CATALOG_URLS = {
  warning_sign: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_warning_sign/',
  intlk_contact_controller: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_intlk_contact_controller/',
  inter_lock_switch: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_inter_lock_switch/',
  toggle_switch: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_toggle_switch/',
  hour_meter: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_hour_meter/',
  radio_meter: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_radio_meter/',
  current_sensor: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_current_sensor/',
  lamp_holder: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_lamp_holder/',
  balast_connector_kit: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_balast_connector_kit/',
  power_supply: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_power_supply/',
  frameing_kit: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_frameing_kit/',
  lamp: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_lamp/',
  uv_repeat_kit: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_uv_repeat_kit/',
  // master lists
  lamp_finish: '/api/v1.0/master/v1.0.0/ahu/uvc_lamp_finish/',
  lamp_length: '/api/v1.0/master/v1.0.0/ahu/uvc_lamp_length/',
  lamp_output: '/api/v1.0/master/v1.0.0/ahu/uvc_lamp_output/',
  lamp_width_calc: '/api/v1.0/master/v1.0.0/ahu/uvc_lamp_width_calc/',
  target: '/api/v1.0/master/v1.0.0/ahu/uvc_target/',
  target_surface: '/api/v1.0/master/v1.0.0/ahu/uvc_target_surface/',
  uvr_lamp_prow_calc: '/api/v1.0/master/v1.0.0/ahu/uvc_uvr_lamp_prow_calc/',
  uvr_lamp_width_calc: '/api/v1.0/master/v1.0.0/ahu/uvc_uvr_lamp_width_calc/',
  // pricing methods (legacy: collection.component_selector.coil.pricing_methods
  // = coil/collection/pricing_methods.js → master/v1.0.0/ahu/pricing_methods/).
  pricing_methods: '/api/v1.0/master/v1.0.0/ahu/pricing_methods/',
  // powered_by
  powered_by: '/api/v1.0/master/v1.0.0/ahu/powered_by/',
} as const;

export type UvcCatalogKey = keyof typeof UVC_CATALOG_URLS;

/**
 * The CMS param GET base (legacy loadParams): relative `ahu/uvc/params/<p>/`
 * mapped-to-CMS → /api/v1.0/cms/<mfg>/v1.0.0/ahu/uvc/params/<p>/. mfg is the
 * GLOBAL current.mfg (id 1 on dev3) — NOT the UVC product mfg 42. product_series
 * appends &part_id=96.
 */
export const UVC_PARAMS_BASE = '/api/v1.0/cms/1/v1.0.0/ahu/uvc/params/';
export const UVC_INTER_LOCK_SWITCH_PARAMS = '/api/v1.0/cms/1/v1.0.0/ahu/uvc_inter_lock_switch/';

/**
 * Required fields gating #rate for mfg 42 (legacy REQUIRED_FIELDS[42]). When
 * any is unfilled the rate button stays non-clickable + the span shows the
 * required asterisk.
 */
export const UVC_REQUIRED_FIELDS = new Set<string>([
  'mfg_id',
  'product_series_id',
  'power_supply_series_id',
  'power_supply_voltage_id',
  'lamp_output_id',
  'lamp_finish_id',
]);

/** Optional "first-set" fields the legacy default-picker auto-selects. */
export const UVC_FIRST_SET_FIELDS = new Set<string>([
  'frameing_kit_id',
  'lamp_series_id',
  'lamp_holder_id',
  'balast_connector_kit_id',
  'uv_repeat_kit_series_id',
  'radio_meter_id',
  'hour_meter_id',
  'current_sensor_id',
  'intlk_contact_controller_id',
  'inter_lock_switch_id',
  'warning_sign_id',
  'uvc_target_target_surface_id',
  'toggle_switch_id',
]);

/**
 * default target_surface_id per section type (legacy initialize switch):
 *   coil→1, filter→2, eliminator→3, evap_pack→4, else→5.
 */
export function defaultTargetIdForSection(sectionType: string | undefined): number {
  switch (sectionType) {
    case 'coil':
      return 1;
    case 'filter':
      return 2;
    case 'eliminator':
      return 3;
    case 'evap_pack':
      return 4;
    default:
      return 5;
  }
}

/**
 * The legacy DependenciesHandler graph (uvc.selector.js:248-430) — VERBATIM,
 * including the ids in its `default_values` comments.
 *
 * This is what puts a value in every Properties / Lamp / Options select the
 * moment the dialog opens. `useDependencyGraph` reproduces
 * `applyParameterNewValues`: keep the saved value if the narrowed list still
 * carries it, else take the first `default_values` entry that survives the
 * narrowing, else — and this is the arm that fills most of the Options tab —
 * auto-select the sole option when the list has exactly one row.
 *
 * Both arms are load-bearing on dev3's catalog:
 *   product_series_id      [749,750,751] → 749 "RLM Extreme"   (default_values)
 *   power_supply_series_id [814,815,816] → 815 "Xbox-PS"       (default_values)
 *   frameing_kit_id        [1]           → "INSTL-KIT"         (sole option)
 *   radio_meter_id         [1]  hour_meter_id [1]  warning_sign_id [1]
 *   intlk_contact_controller_id [1]  toggle_switch_id [1]      (sole option)
 *
 * `uvc_target_target_surface_id`'s default is section-type-derived, so it is
 * injected by the selector (defaultTargetIdForSection) rather than listed here.
 */
export const UVC_DEPENDENCY_GRAPH: DependencyGraphSpec = {
  mfg_id: {
    dependencies: [
      'product_series_id',
      'power_supply_series_id',
      'frameing_kit_id',
      'lamp_series_id',
      'lamp_holder_id',
      'balast_connector_kit_id',
      'uv_repeat_kit_series_id',
      'radio_meter_id',
      'hour_meter_id',
      'power_supply_voltage_id',
      'lamp_output_id',
      'lamp_finish_id',
      'current_sensor_id',
      'intlk_contact_controller_id',
      'inter_lock_switch_id',
      'warning_sign_id',
    ],
    default_values: [UVC_MFG_ID], // 42 UV Resources
  },
  product_series_id: {
    dependencies: [
      'power_supply_series_id',
      'frameing_kit_id',
      'lamp_series_id',
      'lamp_holder_id',
      'balast_connector_kit_id',
      'uv_repeat_kit_series_id',
      'radio_meter_id',
      'hour_meter_id',
      'power_supply_voltage_id',
      'lamp_output_id',
      'lamp_finish_id',
      'current_sensor_id',
      'intlk_contact_controller_id',
      'inter_lock_switch_id',
      'warning_sign_id',
    ],
    default_values: [749], // RLM Extreme
  },
  power_supply_series_id: { dependencies: [], default_values: [815] }, // Xbox-PS
  power_supply_voltage_id: { dependencies: [], default_values: [5] }, // 115/1/60
  lamp_series_id: {
    dependencies: ['lamp_output_id', 'lamp_finish_id'],
    default_values: [817, 830],
  },
  lamp_finish_id: { dependencies: [], default_values: [2] },
  lamp_output_id: { dependencies: [], default_values: [1] },
  lamp_holder_id: { dependencies: [], default_values: [1] },
  balast_connector_kit_id: { dependencies: [], default_values: [4] },
  uv_repeat_kit_series_id: { dependencies: [], default_values: [819] },
  current_sensor_id: { dependencies: [], default_values: [1] },
  radio_meter_id: { dependencies: [], default_values: [] },
  hour_meter_id: { dependencies: [], default_values: [] },
  intlk_contact_controller_id: {
    dependencies: ['inter_lock_switch_id'],
    default_values: [],
  },
  inter_lock_switch_id: { dependencies: [], default_values: [] },
  warning_sign_id: { dependencies: [], default_values: [] },
  frameing_kit_id: { dependencies: [], default_values: [] },
  toggle_switch_id: { dependencies: [], default_values: [] },
};

/**
 * The graph fields whose option ids come from the generic CMS params endpoint
 * (`UVC_PARAMS_BASE<field>/?<parent>=<value>`). The rest are loaded by hand in
 * the selector's loadParams: `inter_lock_switch_id` has its own legacy URL, and
 * `toggle_switch_id` is a CUSTOM_COLLECTION seeded from its master list.
 */
export const UVC_PARAMS_FIELDS = new Set<string>([
  'product_series_id',
  'power_supply_series_id',
  'power_supply_voltage_id',
  'lamp_series_id',
  'lamp_output_id',
  'lamp_finish_id',
  'lamp_holder_id',
  'balast_connector_kit_id',
  'uv_repeat_kit_series_id',
  'current_sensor_id',
  'radio_meter_id',
  'hour_meter_id',
  'intlk_contact_controller_id',
  'warning_sign_id',
  'frameing_kit_id',
]);

/**
 * The ~25 catalog <select> field descriptors driving the Properties / Lamp /
 * Options tabs. `state` is the field name the legacy state model keys on; `cms`
 * is the catalog list (from UVC_CATALOG_URLS) the option set comes from;
 * `valueKey`/`labelKey` map a catalog row to <option value>/<text>.
 *
 * NB: the legacy view rebuilds option sets from the per-parameter CMS params
 * (parameterPossibleIDs × mergeIdsWithCMS) — a dependency-driven filtered list.
 * For Wave-1 read-only render we seed each select from its FULL catalog list
 * (every UVC on dev3 is unrated, so no rate fires and the filtered narrowing is
 * never exercised); the dependency narrowing is a Wave-1 deferral documented in
 * the build report (the full list is a superset, so the saved value always
 * renders).
 */
export interface UvcSelectField {
  /** state model key (the <select name>). */
  name: string;
  /** catalog list key (UVC_CATALOG_URLS). */
  catalog: UvcCatalogKey;
  /** option value source on the catalog row (default 'id'). */
  valueKey?: string;
  /** option label source (default name → value → series → model). */
  labelKey?: string;
  /** model field this select maps onto (for the saved value lookup). */
  modelKey?: string;
  /** allow the "--" None option (optional selects). */
  none?: boolean;
}
